import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import cells from "modules/auth/state";
import LegalLayout, { Callout } from "../../LegalLayout";

const APP_NAME = "Transient Spaces";
const SUPPORT_EMAIL = "morrisgaby987@gmail.com";
const EMAIL_SUBJECT = encodeURIComponent(`${APP_NAME} account deletion request`);

const SECTIONS = [
    {
        id: "in-app",
        title: "Delete from the app",
        highlight: true,
        content: (
            <>
                <ol>
                    <li>Open the {APP_NAME} app and sign in.</li>
                    <li>Go to <strong>Settings</strong>.</li>
                    <li>Tap <strong>Delete account</strong>.</li>
                    <li>
                        Enter your password if asked, type <strong>DELETE</strong> to confirm, then tap{" "}
                        <strong>Delete my account</strong>.
                    </li>
                </ol>
                <Callout tone="warning">
                    Deletion happens immediately and cannot be undone.
                </Callout>
            </>
        ),
    },
    {
        id: "without-app",
        title: "Request deletion without the app",
        content: (
            <p>
                If you no longer have the app installed, email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}?subject=${EMAIL_SUBJECT}`}>{SUPPORT_EMAIL}</a> from the address linked
                to your account with the subject “{APP_NAME} account deletion request”. We will verify that the request
                relates to your account and delete it within 30 days.
            </p>
        ),
    },
    {
        id: "deleted",
        title: "What is deleted",
        content: (
            <>
                <p>When your account is deleted, we remove from our active database:</p>
                <ul>
                    <li>your account profile: email address, optional username and sign-in identity (email and password or Google Sign-In);</li>
                    <li>every space (“Booth”) you created and everything inside it;</li>
                    <li>roadmaps, checkpoints, sub-checkpoints, cycles and tasks;</li>
                    <li>journal entries, reflections and comments;</li>
                    <li>time-tracking records; and</li>
                    <li>membership records and any invitations you created or received.</li>
                </ul>
                <p>Your Firebase Authentication account is deleted at the same time, so you can no longer sign in.</p>
            </>
        ),
    },
    {
        id: "retained",
        title: "What may be kept, and for how long",
        content: (
            <ul>
                <li>
                    <strong>Database backups:</strong> deleted information may remain in DigitalOcean’s automatic backups
                    for up to <strong>seven days</strong>, after which it expires.
                </li>
                <li>
                    <strong>Authentication security records:</strong> Firebase may keep certain security records, such as
                    logged IP addresses, for the periods described in Firebase’s privacy documentation.
                </li>
                <li>
                    <strong>Your device:</strong> information stored locally by the app stays until you sign out, clear
                    the app’s storage or uninstall it.
                </li>
                <li>
                    <strong>Legal records:</strong> we may keep minimal records where necessary to comply with law,
                    resolve disputes, prevent fraud or show that a request was completed.
                </li>
            </ul>
        ),
    },
    {
        id: "questions",
        title: "Questions",
        content: (
            <p>
                Contact us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. More detail is in our{" "}
                <a href="/privacy">Privacy Policy</a>.
            </p>
        ),
    },
];

const AccountDeletionPage = ({ isAuthed }) => (
    <LegalLayout
        isAuthed={isAuthed}
        eyebrow="Your data"
        title={`Delete your ${APP_NAME} account`}
        meta={[{ label: "Developer", value: "Gabriel Morris" }]}
        intro={
            <p>
                This page explains how to delete your {APP_NAME} account and what happens to your data when you do.
            </p>
        }
        sections={SECTIONS}
    />
);

export default strappedConnected(
    AccountDeletionPage,
    { isAuthed: cells.validateToken.selectors.isAuthed },
    {},
    () => ({})
);
